/**
 * Provisioning Pipeline — route-aware
 *
 * Two carrier-registration routes share the same visual chassis:
 *   - 10DLC route: Brand → Campaign → Numbers (3 stages)
 *   - Toll-Free route: Number → Toll-Free Verification (2 stages)
 *
 * The route is chosen by the operator (default seeded by the customer's
 * support ticket), and each stage shows the current status plus the next
 * action available.
 *
 * Designed as a vertical timeline — emphasizes that each route is an *async
 * pipeline* with carrier-approval wait states, not a one-click flow.
 */
import { Button, cn } from '@respectly/ui';
import {
  ShieldCheck,
  Megaphone,
  PhoneCall,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
  Loader2,
  ScrollText,
  PhoneIncoming,
} from 'lucide-react';
import type { TelnyxStatusResponse } from '@respectly/shared-types';
import { TelnyxStatusBadge } from './TelnyxStatusBadge';

export type CarrierRoute = '10dlc' | 'tollfree';

type StageState = {
  state: 'locked' | 'pending' | 'active' | 'done';
  reason?: string;
};

interface ProvisioningPipelineProps {
  route: CarrierRoute;
  onChangeRoute: (route: CarrierRoute) => void;
  status: TelnyxStatusResponse;
  // 10DLC actions
  isCreatingBrand: boolean;
  isRefreshingBrand: boolean;
  isCreatingCampaign: boolean;
  isRefreshingCampaign: boolean;
  onRegisterBrand: () => void;
  onRefreshBrand: () => void;
  onCreateCampaign: () => void;
  onRefreshCampaign: () => void;
  // Toll-Free Verification actions
  isRefreshingTfv: boolean;
  onSubmitTfv: () => void;
  onRefreshTfv: () => void;
  // Common
  onProvisionNumber: () => void;
}

// ─── 10DLC stage state machine ───────────────────────────────────────────────

function tenDlcStageState(
  status: TelnyxStatusResponse,
  stage: 'brand' | 'campaign' | 'numbers',
): StageState {
  if (stage === 'brand') {
    if (!status.businessIdentityComplete)
      return { state: 'locked', reason: 'Business identity incomplete' };
    const s = status.brand?.status?.toUpperCase();
    if (!status.brand) return { state: 'active' };
    if (s === 'APPROVED' || s === 'VERIFIED' || s === 'VETTED_VERIFIED') return { state: 'done' };
    if (s === 'REJECTED') return { state: 'active' };
    return { state: 'pending' };
  }
  if (stage === 'campaign') {
    const brandStatus = status.brand?.status?.toUpperCase();
    const brandReady =
      brandStatus === 'APPROVED' ||
      brandStatus === 'VERIFIED' ||
      brandStatus === 'VETTED_VERIFIED';
    if (!brandReady) return { state: 'locked', reason: 'Awaiting brand approval' };
    const s = status.campaign?.status?.toUpperCase();
    if (!status.campaign) return { state: 'active' };
    if (s === 'APPROVED') return { state: 'done' };
    if (s === 'REJECTED' || s === 'EXPIRED') return { state: 'active' };
    return { state: 'pending' };
  }
  // numbers
  const campaignReady = status.campaign?.status?.toUpperCase() === 'APPROVED';
  if (!campaignReady) return { state: 'locked', reason: 'Awaiting campaign approval' };
  const localNumbers = status.numbers.filter((n) => n.type === 'local' && n.isActive);
  if (localNumbers.length === 0) return { state: 'active' };
  return { state: 'done' };
}

// ─── Toll-Free stage state machine ───────────────────────────────────────────

function tollFreeStageState(
  status: TelnyxStatusResponse,
  stage: 'number' | 'tfv',
): StageState {
  if (stage === 'number') {
    return status.hasTollFreeNumber ? { state: 'done' } : { state: 'active' };
  }
  // tfv
  if (!status.hasTollFreeNumber)
    return { state: 'locked', reason: 'Provision a toll-free number first' };
  if (!status.tollFreeIdentityComplete)
    return { state: 'locked', reason: 'Business address & website incomplete' };
  const s = status.tfv?.status?.toUpperCase();
  if (!status.tfv) return { state: 'active' };
  if (s === 'APPROVED') return { state: 'done' };
  if (s === 'REJECTED' || s === 'EXPIRED') return { state: 'active' };
  return { state: 'pending' };
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ProvisioningPipeline({
  route,
  onChangeRoute,
  status,
  isCreatingBrand,
  isRefreshingBrand,
  isCreatingCampaign,
  isRefreshingCampaign,
  onRegisterBrand,
  onRefreshBrand,
  onCreateCampaign,
  onRefreshCampaign,
  isRefreshingTfv,
  onSubmitTfv,
  onRefreshTfv,
  onProvisionNumber,
}: ProvisioningPipelineProps) {
  const stages = route === '10dlc' ? renderTenDlc() : renderTollFree();
  const trail =
    route === '10dlc' ? 'Brand → Campaign → Numbers' : 'Number → Toll-Free Verification';

  return (
    <div className="relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/40">
      {/* Header rail */}
      <div className="mb-6 flex flex-wrap items-baseline justify-between gap-3 border-b border-slate-100 pb-3 dark:border-slate-800">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            Provisioning pipeline
          </p>
          <h3 className="mt-0.5 text-base font-semibold text-slate-900 dark:text-white">
            Carrier registration route
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <p className="hidden text-[11px] text-slate-400 sm:block">{trail}</p>
          <RouteSegmented value={route} onChange={onChangeRoute} />
        </div>
      </div>

      {/* Pipeline */}
      <div className="relative space-y-0">{stages}</div>
    </div>
  );

  // ─── 10DLC route ───────────────────────────────────────────────────────────

  function renderTenDlc() {
    const brand = tenDlcStageState(status, 'brand');
    const campaign = tenDlcStageState(status, 'campaign');
    const numbers = tenDlcStageState(status, 'numbers');

    return (
      <>
        <Stage
          index={1}
          icon={ShieldCheck}
          title="10DLC Brand"
          subtitle="Identity vetted by carrier (1–7 business days)"
          state={brand.state}
          lockedReason={brand.reason}
          status={status.brand?.status ?? null}
          unsetLabel="Not registered"
          identifier={status.brand?.id ?? null}
        >
          {brand.state === 'locked' ? (
            <p className="text-xs text-slate-500">
              Update business identity (EIN, type, website, address) on the Overview tab first.
            </p>
          ) : !status.brand ? (
            <PrimaryActionButton
              onClick={onRegisterBrand}
              disabled={isCreatingBrand}
              loading={isCreatingBrand}
              label="Register brand"
            />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <RefreshButton onClick={onRefreshBrand} loading={isRefreshingBrand} />
              {brand.state === 'active' && status.brand && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Rejected — review on Telnyx, then refresh
                </span>
              )}
            </div>
          )}
        </Stage>

        <Spine connected={brand.state === 'done'} />

        <Stage
          index={2}
          icon={Megaphone}
          title="A2P Campaign"
          subtitle="Carrier review — typically 1–3 business days after brand approval"
          state={campaign.state}
          lockedReason={campaign.reason}
          status={status.campaign?.status ?? null}
          unsetLabel="Not created"
          identifier={status.campaign?.id ?? null}
        >
          {campaign.state === 'locked' ? (
            <p className="text-xs text-slate-500">
              Brand must reach{' '}
              <span className="font-mono text-[11px]">APPROVED</span> before the campaign can be
              submitted.
            </p>
          ) : !status.campaign ? (
            <PrimaryActionButton
              onClick={onCreateCampaign}
              disabled={isCreatingCampaign}
              loading={isCreatingCampaign}
              label="Create campaign"
            />
          ) : (
            <RefreshButton onClick={onRefreshCampaign} loading={isRefreshingCampaign} />
          )}
        </Stage>

        <Spine connected={campaign.state === 'done'} />

        <Stage
          index={3}
          icon={PhoneCall}
          title="Phone numbers"
          subtitle="Provision dedicated US 10DLC numbers under the approved campaign"
          state={numbers.state}
          lockedReason={numbers.reason}
          status={
            numbers.state === 'done'
              ? `${status.numbers.filter((n) => n.type === 'local' && n.isActive).length} active`
              : numbers.state === 'active'
                ? 'Ready to provision'
                : null
          }
          unsetLabel="Awaiting"
          identifier={null}
          isLast
        >
          {numbers.state === 'locked' ? (
            <p className="text-xs text-slate-500">
              Campaign must be approved before numbers can be assigned to it.
            </p>
          ) : (
            <PrimaryActionButton
              onClick={onProvisionNumber}
              disabled={false}
              loading={false}
              label="Provision a number"
              icon={PhoneCall}
            />
          )}
        </Stage>
      </>
    );
  }

  // ─── Toll-Free route ───────────────────────────────────────────────────────

  function renderTollFree() {
    const number = tollFreeStageState(status, 'number');
    const tfv = tollFreeStageState(status, 'tfv');

    const activeTollFreeCount = status.numbers.filter(
      (n) => n.type === 'tollfree' && n.isActive,
    ).length;

    return (
      <>
        <Stage
          index={1}
          icon={PhoneIncoming}
          title="Toll-Free Number"
          subtitle="Provision a dedicated 8XX number — required before verification can be submitted"
          state={number.state}
          status={
            number.state === 'done'
              ? `${activeTollFreeCount} active`
              : number.state === 'active'
                ? 'Ready to provision'
                : null
          }
          unsetLabel="Awaiting"
          identifier={null}
        >
          <div className="flex flex-wrap items-center gap-2">
            <PrimaryActionButton
              onClick={onProvisionNumber}
              disabled={false}
              loading={false}
              label={number.state === 'done' ? 'Provision another' : 'Provision toll-free number'}
              icon={PhoneIncoming}
              variant={number.state === 'done' ? 'outline' : 'primary'}
            />
            {number.state === 'done' && (
              <span className="text-[11px] text-slate-500">
                Numbers can be added at any time and will share the same TFV submission.
              </span>
            )}
          </div>
        </Stage>

        <Spine connected={number.state === 'done'} />

        <Stage
          index={2}
          icon={ScrollText}
          title="Toll-Free Verification"
          subtitle="Carrier review — typically 2–4 weeks. Required for production-grade delivery."
          state={tfv.state}
          lockedReason={tfv.reason}
          status={status.tfv?.status ?? null}
          unsetLabel="Not submitted"
          identifier={status.tfv?.id ?? null}
          isLast
        >
          {tfv.state === 'locked' ? (
            <p className="text-xs text-slate-500">
              {tfv.reason === 'Provision a toll-free number first'
                ? 'Provision at least one toll-free number above before submitting verification.'
                : 'Update business website and full billing address on the Overview tab first.'}
            </p>
          ) : !status.tfv ? (
            <PrimaryActionButton
              onClick={onSubmitTfv}
              disabled={false}
              loading={false}
              label="Submit verification"
              icon={ScrollText}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <RefreshButton onClick={onRefreshTfv} loading={isRefreshingTfv} />
              {tfv.state === 'active' && status.tfv && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Rejected — review on Telnyx, then resubmit
                </span>
              )}
              {tfv.state === 'active' && !status.tfv && (
                <PrimaryActionButton
                  onClick={onSubmitTfv}
                  disabled={false}
                  loading={false}
                  label="Resubmit"
                  icon={ScrollText}
                  variant="outline"
                />
              )}
            </div>
          )}
        </Stage>
      </>
    );
  }
}

// ─── Route segmented control ─────────────────────────────────────────────────

function RouteSegmented({
  value,
  onChange,
}: {
  value: CarrierRoute;
  onChange: (v: CarrierRoute) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Carrier registration route"
      className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50/80 p-0.5 dark:border-slate-800 dark:bg-slate-950/40"
    >
      <RouteOption value="10dlc" current={value} onClick={() => onChange('10dlc')}>
        10DLC
      </RouteOption>
      <RouteOption value="tollfree" current={value} onClick={() => onChange('tollfree')}>
        Toll-Free
      </RouteOption>
    </div>
  );
}

function RouteOption({
  value,
  current,
  onClick,
  children,
}: {
  value: CarrierRoute;
  current: CarrierRoute;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const active = value === current;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors',
        active
          ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/70 dark:bg-slate-800 dark:text-white dark:ring-slate-700/70'
          : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200',
      )}
    >
      {children}
    </button>
  );
}

// ─── Inner pieces ────────────────────────────────────────────────────────────

interface StageProps {
  index: number;
  icon: typeof ShieldCheck;
  title: string;
  subtitle: string;
  state: 'locked' | 'pending' | 'active' | 'done';
  lockedReason?: string;
  status: string | null;
  unsetLabel: string;
  identifier: string | null;
  isFirst?: boolean;
  isLast?: boolean;
  children: React.ReactNode;
}

function Stage({
  index,
  icon: Icon,
  title,
  subtitle,
  state,
  status,
  unsetLabel,
  identifier,
  children,
}: StageProps) {
  const dimmed = state === 'locked';

  return (
    <div className={cn('flex gap-4', dimmed && 'opacity-50')}>
      {/* Index column */}
      <div className="relative z-10 flex flex-col items-center">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-4',
            state === 'done' &&
              'bg-emerald-500 text-white ring-emerald-100 dark:ring-emerald-950',
            state === 'pending' &&
              'bg-amber-100 text-amber-700 ring-amber-50 dark:bg-amber-900/40 dark:text-amber-300 dark:ring-amber-950/40',
            state === 'active' &&
              'bg-slate-900 text-white ring-slate-100 dark:bg-white dark:text-slate-900 dark:ring-slate-800',
            state === 'locked' &&
              'bg-slate-100 text-slate-400 ring-white dark:bg-slate-800 dark:text-slate-500 dark:ring-slate-900',
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 pb-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
            Stage 0{index}
          </span>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <h4 className="text-[15px] font-semibold text-slate-900 dark:text-white">{title}</h4>
          <TelnyxStatusBadge status={status} unsetLabel={unsetLabel} />
        </div>
        <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>
        {identifier && (
          <p className="mt-1 font-mono text-[10px] uppercase tracking-wide text-slate-400">
            {identifier}
          </p>
        )}
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

function Spine({ connected }: { connected: boolean }) {
  return (
    <div className="relative h-2" aria-hidden="true">
      <div
        className={cn(
          'absolute left-5 -top-3 h-8 w-0.5',
          connected
            ? 'bg-gradient-to-b from-emerald-400 to-emerald-300 dark:from-emerald-600 dark:to-emerald-700'
            : 'bg-slate-200 dark:bg-slate-700',
        )}
      />
    </div>
  );
}

// ─── Action buttons ──────────────────────────────────────────────────────────

function PrimaryActionButton({
  onClick,
  disabled,
  loading,
  label,
  icon: Icon = ArrowRight,
  variant = 'primary',
}: {
  onClick: () => void;
  disabled: boolean;
  loading: boolean;
  label: string;
  icon?: typeof ArrowRight;
  variant?: 'primary' | 'outline';
}) {
  if (variant === 'outline') {
    return (
      <Button size="sm" variant="outline" onClick={onClick} disabled={disabled}>
        {loading ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <Icon className="mr-1.5 h-3.5 w-3.5" />
        )}
        {label}
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
    >
      {loading ? (
        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
      ) : (
        <Icon className="mr-1.5 h-3.5 w-3.5" />
      )}
      {label}
    </Button>
  );
}

function RefreshButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <Button size="sm" variant="outline" onClick={onClick} disabled={loading}>
      <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', loading && 'animate-spin')} />
      Refresh status
    </Button>
  );
}
